<script>
import ExpandableContainer from '@/V2/components/expandable-container/ExpandableContainer.vue';
import ExpandableContainerItem from '@/V2/components/expandable-container/ExpandableContainerItem.vue';
import StatusBar from '@/V2/components/status/StatusBar.vue';
import { Statuses } from '@/V2/components/status/utils';
import { riskLevelToReadableLabel } from '@/constants/riskLevels';
import LoadingBoxes from '@/V2/components/LoadingBoxes.vue';
import {
  mapRunToStatus,
  mapRunToLabel,
  mapRiskLevelToStatus,
  mapRiskLevelToLabel,
} from '@/V2/documents/document-view/document-review-agent/utils';
import AiLabel from '@/V2/components/AiLabel.vue';
import {
  iButton,
  iButtonStyles,
  iButtonTypes,
  iButtonSizes,
} from '@inscribe/signal';
import CustomerEmptyState, {
  CustomerEmptyStateSize,
} from '@/V2/customers/CustomerEmptyState.vue';
import { registerChatAgentHookClickEvent } from '@/events-logger';
import { mapActions, mapGetters } from 'vuex';

export default {
  components: {
    ExpandableContainer,
    ExpandableContainerItem,
    StatusBar,
    LoadingBoxes,
    CustomerEmptyState,
    iButton,
    AiLabel,
  },
  props: {
    summary: {
      type: Object,
    },
    loading: {
      type: Boolean,
    },
    customerHasDocuments: {
      type: Boolean,
    },
    latestCustomerRun: {
      type: Object,
    },
    title: {
      type: String,
      default: 'Analysis',
    },
    buttonText: {
      type: String,
      default: null,
    },
    agentIsRunning: {
      type: Boolean,
      default: false,
    },
    aiLabel: {
      type: String,
      default: 'AI Fraud Analyst',
    },
  },
  data() {
    return {
      mapRunToStatus,
      mapRunToLabel,
      riskLevelToReadableLabel,
      Statuses,
      CustomerEmptyStateSize,
      iButtonStyles,
      iButtonTypes,
      iButtonSizes,
    };
  },
  computed: {
    ...mapGetters([
      'isChatAgentEnabled',
      'currentConversationUuid',
      'getIsGeneratingForConversationUuid',
    ]),
    showLoadingState() {
      return this.loading;
    },
    runStatus() {
      return mapRiskLevelToStatus(this.summary?.risk_level);
    },
    statusLabel() {
      return mapRiskLevelToLabel(this.summary?.risk_level);
    },
    hasEmptyState() {
      return !this.summary?.risk_summary && !this.latestCustomerRun;
    },
    chatIsGenerating() {
      return this.getIsGeneratingForConversationUuid(
        this.currentConversationUuid,
      );
    },
  },
  methods: {
    ...mapActions(['submitConversationMessage', 'openChat']),
    onRunDeeperAnalysisClicked() {
      this.openChat();
      this.submitConversationMessage({
        conversationUuid: this.currentConversationUuid,
        userMessageContent:
          'Run a deeper analysis of this customer for fraud risk',
        contextCustomerUuids: [this.$route.params.customer_id],
        contextDocumentIds: [],
      });
      registerChatAgentHookClickEvent(
        'customer-fraud-summary-suggested-prompt',
      );
    },
  },
};
</script>

<template>
  <div class="customer-fraud-summary">
    <ExpandableContainer :title="title" :expandable="false">
      <template #tools>
        <i-button
          v-if="buttonText"
          :text="buttonText"
          :size="iButtonSizes.SMALL"
          :type="iButtonTypes.NEUTRAL"
          :button-style="iButtonStyles.OUTLINE"
          @button-click="(e) => $emit('button-click', e)"
        />
      </template>

      <template #items>
        <!-- loading state -->
        <div
          v-if="showLoadingState || agentIsRunning"
          class="summary slot-content d-flex flex-column i-p-9 i-gap-6"
          data-testid="customer-fraud-summary-loading"
        >
          <div class="d-flex flex-column i-gap-6 flex-grow-1">
            <status-bar
              :state="Statuses.LOADING"
              :label="agentIsRunning ? 'Analyzing...' : 'Loading...'"
            />
            <loading-boxes
              :config="{ lengths: [540, 426, 467], repeat: 1 }"
            ></loading-boxes>
          </div>

          <ai-label :text="aiLabel" />
        </div>

        <!-- empty state -->
        <customer-empty-state
          v-else-if="hasEmptyState"
          class="summary slot-content"
          :title="
            customerHasDocuments
              ? 'Fraud Rating Unavailable'
              : 'No Fraud Rating'
          "
          :description="
            customerHasDocuments
              ? 'A rating is not currently available for this customer'
              : 'Upload documents to begin analysis'
          "
          icon="medium-user-alert"
          :size="CustomerEmptyStateSize.SMALL"
          :show-button="false"
          data-testid="customer-fraud-summary-empty-state"
        />

        <div
          v-else
          class="summary slot-content d-flex flex-column align-items-start space-between i-p-9 i-gap-6"
        >
          <status-bar
            v-if="summary?.risk_level"
            :state="runStatus"
            :label="statusLabel"
            data-testid="customer-fraud-summary-status-bar"
          />
          <div
            class="summary-text i-text-body-2 i-text-neutral-120 flex-grow-1"
            data-testid="customer-fraud-summary-text"
          >
            {{
              summary?.risk_summary
                ? summary.risk_summary
                : latestCustomerRun?.output?.rationale
            }}
          </div>

          <i-button
            v-if="isChatAgentEnabled"
            :type="iButtonTypes.NEUTRAL"
            :button-style="iButtonStyles.SOLID_LIGHT"
            :size="iButtonSizes.SMALL"
            :label="'Run deeper analysis'"
            @button-click="onRunDeeperAnalysisClicked"
            :disabled="chatIsGenerating"
          ></i-button>
        </div>
      </template>
    </ExpandableContainer>
  </div>
</template>

<style lang="scss" scoped>
.summary {
  height: 100%;
  min-height: 196px;
  background: $white-base;
}
</style>
